import { Link } from '@tanstack/react-router'
import { useSubscription } from '@/entities/billing/model/queries'
import { usePermissions } from '@/entities/session/hooks/usePermissions'
import { isTrialEnded, isTrialOnly, trialDaysLeft } from '@/entities/billing/model/trial'
import {
  BILLING_PATH,
  endedMessage,
  inactiveMessage,
  PAST_DUE_MESSAGE,
  TRIAL_ENDED_MESSAGE,
} from '@/shared/api/billingErrors'
import { PERMISSIONS } from '@/shared/config/permissions'
import { formatBillingDate } from '../lib/formatDate'

function trialingMessage(trialEndsAt: string | null, daysLeft: number | null): string {
  const ends = formatBillingDate(trialEndsAt)
  if (daysLeft === null || !ends) return ends ? `Trial ends ${ends}.` : 'Trial in progress.'
  // The date has passed but the expiry job has not moved the account yet (it runs on an interval),
  // so the status still says Trialing. Go by the date: the trial is over, not about to be.
  if (daysLeft <= 0) return 'Your free trial has ended. Choose a plan to keep making changes.'
  if (daysLeft <= 3) {
    return `Your free trial ends in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}. Choose a plan to keep making changes.`
  }
  return `Trial ends ${ends}.`
}

/**
 * Deliberately not dismissible. Someone whose office has gone read-only needs to know why
 * every button vanished, and a banner they closed yesterday does not tell them.
 *
 * Only people who can view billing see it: an account's payment standing is not for everyone on
 * the roster to read. Anyone else who is blocked gets the neutral toast from the API client.
 */
export function SubscriptionBanner() {
  const { hasPermission } = usePermissions()
  const canView = hasPermission(PERMISSIONS.BillingView)
  const { data } = useSubscription(canView)

  if (!canView || !data) return null
  if (data.status === 'Active') return null

  const trialEnded = isTrialEnded(data)
  const daysLeft = data.status === 'Trialing' ? trialDaysLeft(data.trialEndsAt) : null
  // Soft nudge only in the last stretch; before that the plain end date is all a trial needs.
  const endingSoon = daysLeft !== null && daysLeft <= 3
  // Ended by the date even though the status has not caught up: asks for a plan, same as a lapsed trial.
  const trialLapsed = trialEnded || (daysLeft !== null && daysLeft <= 0)
  // A past-due paid account owes a balance; a lapsed trial owes nothing and is asked to choose a plan.
  const pastDuePaid = data.status === 'PastDue' && !trialEnded
  // A subscription that has ENDED is read-only and leaving, not suspended. It owes nothing, so it is
  // never told to settle or restart; what it needs is the way to its data and the deletion date.
  const ended = data.status === 'Canceled'
  const actionLabel = ended
    ? 'Download your data'
    : trialLapsed
      ? 'Choose a plan'
      : pastDuePaid
        ? 'Settle balance'
        : 'View plan'

  const message = ended
    ? endedMessage(formatBillingDate(data.windDownEndsAt))
    : trialEnded
      ? TRIAL_ENDED_MESSAGE
      : data.status === 'PastDue'
        ? PAST_DUE_MESSAGE
        : data.status === 'Suspended'
          ? inactiveMessage(isTrialOnly(data))
          : trialingMessage(data.trialEndsAt, daysLeft)

  // A lapsed or ending trial is a prompt, not a failure, so it takes the amber tone rather than red.
  // An ended subscription takes amber too and deliberately not red: the account is working exactly as
  // the customer asked it to, and red would read as a fault on an account that is simply read-only.
  const tone = trialEnded || endingSoon || ended
    ? 'border-warn-bd bg-warn-bg text-warn'
    : data.status === 'Trialing'
      ? 'border-line bg-hover text-ink'
      : 'border-bad-bd bg-bad-bg text-bad'

  return (
    <div role="status" className={`flex items-center justify-between gap-4 border-b px-4 py-2 text-sm ${tone}`}>
      <span>{message}</span>
      {/* The page is gated on billing.view, which is what got us here, so the link always lands. */}
      <Link to={BILLING_PATH} className="shrink-0 underline underline-offset-2">
        {actionLabel}
      </Link>
    </div>
  )
}
